import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Info, Eye } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAuth } from '../lib/auth-context';
import { TravelProfile } from '../types/matching';
import { MatchingFilters } from '../components/matching/MatchingFilters';
import { MatchingList } from '../components/matching/MatchingList';
import { CreateListingForm } from '../components/matching/CreateListingForm';
import { collection, query, where, getDocs, doc, getDoc, orderBy, limit, startAfter, getCountFromServer } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { createTravelProfile } from '../lib/firebase-service';
import * as amplitude from '@amplitude/analytics-browser';

// Helper function to get liked profiles
const getLikedProfiles = async (userId: string) => {
  try {
    const profilesRef = collection(db, 'travel_profiles');
    const q = query(
      profilesRef,
      where('likes', 'array-contains', userId),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      isOwnProfile: doc.data().userId === userId
    })) as TravelProfile[];
  } catch (error) {
    console.error('Error fetching liked profiles:', error);
    return [];
  }
};

export function MatchingPage() {
  const { user } = useAuth();
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = React.useState<TravelProfile[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = React.useState(false);
  const [hasExistingProfile, setHasExistingProfile] = React.useState(false);
  const [existingProfileId, setExistingProfileId] = React.useState<string | null>(null);
  const [lastVisible, setLastVisible] = React.useState<any>(null);
  const [hasMore, setHasMore] = React.useState(true);
  const [currentFilters, setCurrentFilters] = React.useState<any>({});
  const [isLikedOnly, setIsLikedOnly] = React.useState(false);
  const PROFILES_PER_PAGE = 11;
  const [totalProfiles, setTotalProfiles] = React.useState<number>(0);
  const hasTrackedRef = React.useRef(false);

  // Initial load of profiles
  React.useEffect(() => {
    const fetchProfiles = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Vérifier si l'utilisateur a déjà un profil
        const userProfileQuery = query(
          collection(db, 'travel_profiles'),
          where('userId', '==', user?.uid),
          where('isActive', '==', true),
          limit(1)
        );
        const userProfileSnapshot = await getDocs(userProfileQuery);
        
        if (!userProfileSnapshot.empty) {
          setHasExistingProfile(true);
          // Store the profile ID for navigation
          setExistingProfileId(userProfileSnapshot.docs[0].id);
        }

        // Get total count of profiles
        const totalCountQuery = query(
          collection(db, 'travel_profiles'),
          where('isActive', '==', true)
        );
        const totalSnapshot = await getCountFromServer(totalCountQuery);
        setTotalProfiles(totalSnapshot.data().count);

        const profilesRef = collection(db, 'travel_profiles');
        const q = query(
          profilesRef,
          where('isActive', '==', true),
          orderBy('createdAt', 'desc'),
          limit(PROFILES_PER_PAGE)
        );

        const snapshot = await getDocs(q);
        
        // Set the last visible document for pagination
        if (!snapshot.empty) {
          setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
          setHasMore(snapshot.docs.length === PROFILES_PER_PAGE);
        } else {
          setHasMore(false);
        }
        
        const fetchedProfiles = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as TravelProfile[];

        setProfiles(fetchedProfiles);
      } catch (error) {
        console.error('Error fetching profiles:', error);
        setError('Impossible de charger les profils pour le moment');
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchProfiles();
    }
  }, [user]);

  React.useEffect(() => {
    if (!hasTrackedRef.current) {
      // Analytics.
      amplitude.track("PVT Mate Displayed");
      hasTrackedRef.current = true;
    }
  }, []);

  // Redirect to home if not authenticated
  React.useEffect(() => {
    if (!user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const handleFilterChange = async (filters: any) => {
    try {
      setIsLoading(true);
      setLastVisible(null);
      setHasMore(true);
      setIsLikedOnly(!!filters.likedOnly);
      setError(null);
      setCurrentFilters(filters);

      // If likedOnly is true, use a different approach to get profiles
      if (filters.likedOnly) {
        const likedProfiles = await getLikedProfiles(user?.uid || '');
        
        // Apply additional filters to liked profiles
        let filteredProfiles = likedProfiles;
        
        if (filters.destination) {
          filteredProfiles = filteredProfiles.filter(
            profile => profile.preferences.destination === filters.destination
          );
        }
        
        if (filters.pvtStatus) {
          filteredProfiles = filteredProfiles.filter(
            profile => profile.pvtStatus === filters.pvtStatus
          );
        }
        
        if (filters.objective) {
          filteredProfiles = filteredProfiles.filter(
            profile => profile.objectives?.includes(filters.objective)
          );
        }
        
        setProfiles(filteredProfiles);
        setTotalProfiles(filteredProfiles.length);
        setHasMore(false); // No pagination for liked profiles
      } else {
        // Build query conditions based on filters
        const profilesRef = collection(db, 'travel_profiles');
        
        // Start with base condition
        let queryConditions: any[] = [where('isActive', '==', true)];
        
        // Add filters with array-contains for objectives
        if (filters.destination) {
          queryConditions.push(where('preferences.destination', '==', filters.destination));
        }
        
        if (filters.pvtStatus) {
          queryConditions.push(where('pvtStatus', '==', filters.pvtStatus));
        }
        
        if (filters.objective) {
          queryConditions.push(where('objectives', 'array-contains', filters.objective));
        }
        
        // Get total count with all filters
        const totalCountQuery = query(
          profilesRef,
          ...queryConditions
        );
        
        const totalSnapshot = await getCountFromServer(totalCountQuery);
        setTotalProfiles(totalSnapshot.data().count);

        // Create the main query with ordering and limit
        const q = query(
          profilesRef,
          ...queryConditions,
          orderBy('createdAt', 'desc'),
          limit(PROFILES_PER_PAGE)
        );
        
        const snapshot = await getDocs(q);
        
        // Set the last visible document for pagination
        if (!snapshot.empty) {
          setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
          setHasMore(snapshot.docs.length === PROFILES_PER_PAGE);
        } else {
          setHasMore(false);
        }
        
        const fetchedProfiles = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          isOwnProfile: doc.data().userId === user?.uid
        })) as TravelProfile[];

        setProfiles(fetchedProfiles);
      }

      // Analytics
      const eventProperties = {
        destination: filters.destination,
        pvtStatus: filters.pvtStatus,
        objective: filters.objective,
        likedOnly: filters.likedOnly
      };
      amplitude.track('PVT Mate Filters', eventProperties);
    } catch (error) {
      console.error('Error fetching profiles:', error);
      setError('Impossible de charger les profils pour le moment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!lastVisible || isLoadingMore) return;
    
    // Don't load more if we're in liked-only mode
    if (isLikedOnly) return;
    
    try {
      setIsLoadingMore(true);
      
      const profilesRef = collection(db, 'travel_profiles');
      
      // Start with the base condition
      let queryConditions: any[] = [where('isActive', '==', true)];
      
      // Apply the same filters as the initial query
      if (currentFilters.destination) {
        queryConditions.push(where('preferences.destination', '==', currentFilters.destination));
      }
      
      if (currentFilters.pvtStatus) {
        queryConditions.push(where('pvtStatus', '==', currentFilters.pvtStatus));
      }
      
      if (currentFilters.objective) {
        queryConditions.push(where('objectives', 'array-contains', currentFilters.objective));
      }
      
      const q = query(
        profilesRef,
        ...queryConditions,
        orderBy('createdAt', 'desc'),
        startAfter(lastVisible),
        limit(PROFILES_PER_PAGE)
      );
      
      const snapshot = await getDocs(q);
      
      // Update the last visible document
      if (!snapshot.empty) {
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        setHasMore(snapshot.docs.length === PROFILES_PER_PAGE);
      } else {
        setHasMore(false);
      }
      
      const newProfiles = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        username: doc.data().userId === user?.uid ? userInfo?.user?.username : doc.data().username,
        isOwnProfile: doc.data().userId === user?.uid
      })) as TravelProfile[];
      
      // Append new profiles to existing ones
      setProfiles(prev => [...prev, ...newProfiles]);
    } catch (error) {
      console.error('Error loading more profiles:', error);
      setError('Impossible de charger plus de profils');
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Helper function to calculate age
  const getAge = (birthDate: any): number => {
    if (!birthDate) return 0;
    const date = birthDate.toDate ? birthDate.toDate() : new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
      age--;
    }
    return age;
  };

  const handleLike = async (profile: TravelProfile) => {
    // TODO: Implement like logic
    console.log('Liked profile:', profile.id);
  };

  const handlePass = async (profile: TravelProfile) => {
    // TODO: Implement pass logic
    console.log('Passed profile:', profile.id);
  };

  const handleMessage = async (profile: TravelProfile) => {
    // TODO: Implement message logic
    console.log('Message to profile:', profile.id);
  };

  const handleCreateListing = async (data: any) => {
    try {
      if (!user) return;
      setError(null);
      setIsLoading(true);
      
      const newProfileId = await createTravelProfile({
        userId: user.uid,
        username: userInfo?.user?.username,
        ...data
      });
      
      // Récupérer le nouveau profil créé
      const profileRef = doc(db, 'travel_profiles', newProfileId);
      const profileDoc = await getDoc(profileRef);
      
      if (profileDoc.exists()) {
        // Ajouter le nouveau profil à la liste avec isOwnProfile=true
        const newProfile = {
          id: profileDoc.id,
          ...profileDoc.data(),
          isOwnProfile: true
        } as TravelProfile;
        
        setProfiles(prev => [newProfile, ...prev]);
        setHasExistingProfile(true);
        setExistingProfileId(newProfileId);
      }
      
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error creating listing:', error);
      setError('Impossible de créer l\'annonce. Veuillez vérifier vos informations et réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewOwnProfile = () => {
    if (existingProfileId) {
      navigate(`/matching/${existingProfileId}`);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col pb-mobile">
      <Navbar 
        onLogin={() => {}} 
        onAdminClick={() => {}}
        onHomeClick={() => {}}
        isAdminMode={false}
      />

      {/* Hero Section */}
      <div className="hidden sm:flex relative min-h-[500px] items-center">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1527631746610-bca00a040d60?q=80&w=2574&auto=format&fit=crop"
            alt=""
            className="w-full h-full object-cover opacity-90 dark:opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-gray-900/70 to-gray-900/95"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-4 sm:mb-6">
              <Users className="w-4 h-4 text-primary-400" />
              <span className="text-xs sm:text-sm font-medium text-white">Compagnons de voyage</span>
            </div>

            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Trouvez votre compagnon de PVT
            </h1>

            <p className="text-lg text-white/90 leading-relaxed">
              Rencontrez d'autres voyageurs qui partagent vos dates, votre destination et votre style de voyage.
            </p>
          </div>
        </div>
      </div>

      <main className="flex-1 pt-20 sm:pt-0 pb-6 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 px-4 py-3 order-2 sm:order-1 sm:w-auto">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                  <Users className="w-5 h-5 text-primary-500 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Annonces</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {isLoading ? (
                      <span className="inline-block w-8 h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></span>
                    ) : (
                      totalProfiles
                    )}
                  </p>
                </div>
              </div>
            </div>
            
            {hasExistingProfile ? (
              <div className="flex items-center justify-between w-full bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-100 dark:border-amber-800/30 p-4 order-3">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Vous avez déjà une annonce active
                  </p>
                </div>
                <button
                  onClick={handleViewOwnProfile}
                  className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors shadow-sm border border-amber-200/50 dark:border-amber-700/30"
                >
                  <Eye className="w-4 h-4" />
                  <span className="text-sm font-medium">Voir mon profil</span>
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl transition-colors shadow-sm hover:shadow-md w-full sm:w-auto order-3"
              >
                <Plus className="w-5 h-5" />
                <span className="font-medium">Créer une annonce</span>
              </button>
            )}
            
            <div className="flex-1 order-1 sm:order-2">
              <MatchingFilters onFilterChange={handleFilterChange} currentUserId={user?.uid} />
            </div>
          </div>

          <MatchingList
            profiles={profiles}
            onLike={handleLike}
            onPass={handlePass}
            onMessage={handleMessage}
            isLoading={isLoading}
            error={error}
            currentUserId={user?.uid}
          />
          
          {/* Load More Button */}
          {hasMore && !isLoading && !isLikedOnly && (
            <div className="flex justify-center mt-8">
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingMore ? (
                  <>
                    <div className="w-5 h-5 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin"></div>
                    <span>Chargement...</span>
                  </>
                ) : (
                  <>
                    <span>Voir plus d'annonces</span>
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          )}
          
          {/* No more results message */}
          {!hasMore && profiles.length > 0 && !isLoading && (
            <div className="text-center mt-8 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200/50 dark:border-gray-700/50">
              <p className="text-gray-600 dark:text-gray-400">
                Vous avez vu toutes les annonces disponibles
              </p>
            </div>
          )}
        </div>
      </main>
      
      {showCreateForm && (
        <CreateListingForm
          onSubmit={handleCreateListing}
          onClose={() => setShowCreateForm(false)}
        />
      )}

      <Footer />
    </div>
  );
}